import { useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { connectorsApi } from '../../api/connectors'
import { RefusedError } from '../../api/client'
import { useLandscape } from '../../hooks/useLandscape'
import { useSpoke } from '../../hooks/useSpoke'
import { CONTACT_LIST_ACCEPT, CONTACT_LIST_HINT, refuseContactList } from '../../lib/files'

// IMPORT DATA. Two ways to get a contact list out of a system you already use — your LinkedIn
// connections, your firm's CRM — and one thing that happens to the file either way: it becomes a
// batch of proposals on Onboard Contacts, and nothing reaches your contacts until you confirm the
// rows (DEC-2026-09-10-06). It takes an Excel workbook as readily as a CSV; a person with a workbook
// in their hand used to be stopped here by "it's got to be a csv file" (REQ-063).

export function Connectors() {
  const spoke = useSpoke()
  // The firm's Snowflake is a different kind of thing from the two uploads below: a connection, made once by an
  // admin. The API says who may make it (the landscape's warehouse system); for anybody else the card does not exist.
  const warehouse = useLandscape().data?.systems.find((s) => s.id === 'warehouse')
  return (
    <section className="page connectors">
      <header className="deals-head">
        <div>
          <p className="kicker"><Link to="/landscape">Digital Landscape</Link> · bring in your data</p>
          <h1>Import Data</h1>
          <p className="lede">
            Upload an Excel workbook, a LinkedIn Connections export or a CRM contact export. Scout cleans it, folds the
            duplicates together and matches every row against what it already knows, then hands you the list to work
            through. <strong>Nothing reaches your contacts until you confirm the rows.</strong>
          </p>
        </div>
      </header>
      {warehouse?.canConnect === true && (
        <div className="card path-card">
          <div className="path-head"><div><p className="kicker">Firm data</p><h2>Snowflake</h2></div></div>
          <p>
            One read-only connection to the firm's Snowflake, made once for everybody. Scout generates the key and writes
            the steps for your Snowflake administrator. Nothing is uploaded, and Scout never writes to Snowflake.
          </p>
          <Link className="button" to="/connectors/snowflake">{warehouse.status === 'connected' ? 'Open the Snowflake connection' : 'Set up Snowflake'}</Link>
        </div>
      )}
      <div className="paths">
        <ImportCard
          spoke={spoke}
          kind="LinkedIn"
          blurb="Export your connections from LinkedIn (Settings → Data privacy → Get a copy of your data → Connections). Drop the Connections.csv here; Scout adds them to your network with their company and title."
          hint="LinkedIn → Connections.csv"
        />
        <ImportCard
          spoke={spoke}
          kind="CRM or a workbook"
          blurb="A contact list you keep yourself, or an export from DealCloud, HubSpot or Salesforce. An .xlsx, .xls or .csv — Scout finds the header row itself, so a title and a blank line above it are fine."
          hint="Your workbook · DealCloud / HubSpot / Salesforce → contacts.csv"
        />
      </div>
      <p className="muted small">
        Both of these land on <Link to="/onboarding">Onboard Contacts</Link>, where you accept the rows you want and
        turn down the ones you do not, with the reason. That is the only way anything from a file reaches your contacts.
      </p>
    </section>
  )
}

function ImportCard({ spoke, kind, blurb, hint }: { spoke: string; kind: string; blurb: string; hint: string }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const [fileName, setFileName] = useState<string | null>(null)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [progress, setProgress] = useState(0)

  const upload = useMutation({
    mutationFn: (file: File) => connectorsApi.upload(spoke, file, setProgress),
    // straight to the batch: the rows are the point, and the staging job is named on that screen
    onSuccess: (r) => navigate(`/onboarding/${encodeURIComponent(r.batch.batchId)}`),
  })

  const pick = (file?: File) => {
    if (!file) return
    // A file this screen cannot read is refused HERE, by name, with the screens that read one
    // (REQ-063-AC3) — never by bouncing the person off the server with the word "csv".
    const no = refuseContactList(file.name)
    setRefusal(no)
    if (no) return
    setFileName(file.name)
    setProgress(0)
    upload.mutate(file)
  }

  return (
    <div className="card path-card">
      <div className="path-head"><div><p className="kicker">Connector</p><h2>{kind}</h2></div></div>
      <p>{blurb}</p>
      <div
        className="dropzone"
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click() }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0]) }}
      >
        <input ref={inputRef} type="file" accept={CONTACT_LIST_ACCEPT} hidden onChange={(e) => { pick(e.target.files?.[0]); e.currentTarget.value = '' }} />
        {upload.isPending
          ? <><strong>Uploading {fileName}…</strong><span className="muted">{Math.round(progress * 100)}%</span></>
          : <><strong>Drop a workbook or a CSV, or click to choose</strong><span className="muted">{hint}</span></>}
      </div>
      {refusal && <p className="error">{refusal}</p>}
      {upload.isError && <p className="error">{upload.error instanceof RefusedError ? upload.error.refusal.message : upload.error.message}</p>}
      <p className="muted small">{CONTACT_LIST_HINT}. Scout stages every row for you to confirm; nothing lands from here on its own.</p>
    </div>
  )
}
